import React, { lazy } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import NotFound from "components/NotFound";
import Changeset from "components/changesets/Changeset";
import ZoningDetailsModal from "components/guides/ZoningDetails/ZoningDetailsModal";
import RequirementPayment from "components/payments/PaymentPage";
import ApplicationDetails from "components/projects/ApplicationDetails";
import Attachments from "components/projects/Attachments";
import Handoff from "components/projects/Handoff";
import RequirementFormFillingPage from "components/projects/RequirementFormFillingPage";
import ResumeProjectRedirect from "components/projects/ResumeProjectRedirect";
import ZoningCheckPage from "components/projects/pages/ZoningCheck";
import ZoningVerificationPage from "components/projects/pages/ZoningVerification";
import ProcessPayment from "components/projects/requirement/Process";
import SubmitApplication from "components/projects/requirement/SubmitApplication";
import {
  ProjectIsScopeable,
  RequireRealUser,
  RequirementApplicationIsInProgress,
  RequirementApplicationIsPayable,
  RequirementIsIssued,
} from "components/utilities/guards";

import HotKeys from "./HotKeys";
import ScopeProject from "./ScopeProject";
import RequirementSummaryPage from "./requirement/RequirementSummaryPage";

const IssuedRequirementApplication = lazy(() =>
  import(/* webpackPrefetch: true */ "components/projects/IssuedRequirementApplicationPage"),
);
const Guides = lazy(() => import(/* webpackPrefetch: true */ "components/guides/GuidePageRouter"));
const PrintableRequirementSummary = lazy(() =>
  import("components/printables/PrintableRequirementSummary"),
);
const PrintableProjectSummary = lazy(() => import("components/printables/PrintableProjectSummary"));

const ProjectsRoutes = () => {
  const match = useRouteMatch();

  return (
    <>
      <HotKeys />
      <Switch>
        <Route
          exact
          path={`${match.path}/applications/:requirementApplicationID/issued_requirement`}
          render={(props) => (
            <RequirementIsIssued>
              <IssuedRequirementApplication {...props} />
            </RequirementIsIssued>
          )}
        />

        <Route
          exact
          path={`${match.path}/applications/:requirementApplicationID/submitted_requirement`}
          component={PrintableRequirementSummary}
        />

        <Route path={`${match.path}/guide/location/zoning-details`}>
          <ProjectIsScopeable>
            <ZoningDetailsModal />
          </ProjectIsScopeable>
        </Route>
        <Route path={`${match.path}/guide/:guidePage*`}>
          <ProjectIsScopeable>
            <Guides />
          </ProjectIsScopeable>
        </Route>

        <Route path={`${match.path}/attachments`}>
          <RequireRealUser>
            <Attachments />
          </RequireRealUser>
        </Route>

        <Route exact path={`${match.path}/zoning-verification`}>
          <ZoningVerificationPage />
        </Route>
        <Route exact path={`${match.path}/zoning-check`}>
          <ZoningCheckPage />
        </Route>

        <Route exact path={`${match.path}/summary`}>
          <Redirect to={`${match.url}/apply/summary`} />
        </Route>

        <Route
          exact
          path={`${match.path}/requirements/:requirementSlug/summary`}
          render={(props) => (
            <RequireRealUser>
              <RequirementSummaryPage {...props} />
            </RequireRealUser>
          )}
        />

        <Route exact path={`${match.path}/requirements/:requirementSlug/payment/:invoiceID?`}>
          <RequirementApplicationIsPayable>
            <RequirementPayment />
          </RequirementApplicationIsPayable>
        </Route>

        <Route
          exact
          path={`${match.path}/requirements/:requirementSlug/process/:id?`}
          render={(props) => (
            <RequireRealUser>
              <ProcessPayment {...props} />
            </RequireRealUser>
          )}
        />

        <Route
          exact
          path={`${match.path}/:requirementOrDirect(requirements|direct)/:requirementSlug/submit`}
        >
          <RequireRealUser>
            <ScopeProject>
              <SubmitApplication />
            </ScopeProject>
          </RequireRealUser>
        </Route>

        <Route
          exact
          path={`${match.path}/:requirementOrDirect(requirements|direct)/:requirementSlug`}
        >
          <RequirementApplicationIsInProgress>
            <RequirementFormFillingPage />
          </RequirementApplicationIsInProgress>
        </Route>

        <Route exact path={`${match.path}/application/:requirementID`}>
          <ApplicationDetails />
        </Route>

        <Route path={`${match.path}/apply`}>
          <Handoff />
        </Route>
        <Route exact path={`${match.path}/printable_summary`}>
          <PrintableProjectSummary />
        </Route>

        <Route exact path={`${match.path}/changesets/:changesetID`}>
          <RequireRealUser>
            <Changeset />
          </RequireRealUser>
        </Route>

        <Route exact path={`${match.path}`}>
          <ResumeProjectRedirect />
        </Route>

        <Route component={NotFound} />
      </Switch>
    </>
  );
};

export default ProjectsRoutes;
